import { DownloadOutlined, InboxOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Button, Form, Modal, Space, Typography, Upload, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../api/utils';
import useAuth from '../../auth/useAuth';
import { SURVEY_ID_HEADER } from '../../constants';
import { BluePrimary } from '../../styles';

const { Dragger } = Upload;
const { Text, Paragraph } = Typography;

const StyledFormItem = styled(Form.Item)`
  padding-top: 12px;
`;

const StyledInboxOutlined = styled(InboxOutlined)`
  display: block;
  color: ${BluePrimary};
  font-size: 48px;
  margin-bottom: 1rem;
`;

const StyledText = styled(Text)`
  color: ${BluePrimary};
  font-size: 13px;
`;

function TranscriptGridModal({ visible, closeModal, surveyId }) {
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const { getAccessTokenSilently } = useAuth();

  const onFinish = async () => {
    if (!file) return;

    setReprocessing(true);
    try {
      const token = await getAccessTokenSilently();
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}api/v1/surveys/${surveyId}/reprocess/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          [SURVEY_ID_HEADER]: surveyId,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Reprocess failed');
      }

      message.success('Survey is being reprocessed. This may take a few minutes.');
      closeModal();
    } catch {
      message.error('Something went wrong reprocessing the survey');
    } finally {
      setReprocessing(false);
    }
  };

  const handleDownloadTranscriptGrid = async () => {
    setDownloading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}api/v1/surveys/${surveyId}/transcript-grid/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          [SURVEY_ID_HEADER]: surveyId,
        },
      });
      if (!response.ok) {
        throw new Error('Download failed');
      }
      const filename =
        response.headers.get('Content-Disposition')?.split('filename=')[1] ||
        `transcript-grid-${surveyId}.csv`;
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Something went wrong downloading the transcript grid');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      title="Access or edit your transcript grid"
      open={visible}
      onOk={() => {
        form
          .validateFields()
          .then(onFinish)
          .catch(() => {});
      }}
      onCancel={closeModal}
      okText="Reprocess"
      okButtonProps={{ disabled: !file, loading: reprocessing }}
      cancelButtonProps={{ disabled: reprocessing }}
      destroyOnClose
      maskClosable={false}
      closable={false}
    >
      <Form
        onFinish={onFinish}
        autoComplete="off"
        layout="vertical"
        form={form}
        requiredMark={false}
      >
        <StyledFormItem>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadTranscriptGrid}
            loading={downloading}
          >
            Download your transcript grid
          </Button>
        </StyledFormItem>
        <Form.Item
          name="file"
          label={<Text strong>Upload edited transcript grid:</Text>}
          rules={[{ required: true, message: 'Please attach a CSV file.' }]}
          valuePropName="fileList"
          getValueFromEvent={(e) => [e.file]}
        >
          <Paragraph>
            If needed, you can upload an edited version of your Transcript Grid for Fathom to
            reprocess.
            <b>This will overwrite any coding you have completed so far.</b>
          </Paragraph>
          <Dragger
            accept=".csv"
            beforeUpload={() => false}
            onChange={(e) => setFile(e.file)}
            showUploadList={false}
          >
            <StyledInboxOutlined />
            <Space direction="vertical" size={2}>
              {file ? (
                <>
                  <Space>
                    <Text type="secondary">
                      <PaperClipOutlined />
                    </Text>
                    {file.name}
                  </Space>
                  <StyledText>Drag or choose a new file</StyledText>
                </>
              ) : (
                <>
                  <Text>Drag an edited Transcript Grid to this area to upload</Text>
                  <StyledText>Or select a file from your computer</StyledText>
                </>
              )}
            </Space>
          </Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
}

TranscriptGridModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  surveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default TranscriptGridModal;
